<template>
  <div class="account-identity-balance">
    <router-link
      v-if="profileDestination"
      :to="profileDestination"
      class="account-identity-balance__avatar"
      :aria-label="`Open ${displayName}'s profile`"
    >
      <Avatar :user="resolvedUser" size="medium" decorative />
    </router-link>
    <span v-else class="account-identity-balance__avatar">
      <Avatar :user="resolvedUser" size="medium" decorative />
    </span>

    <div class="account-identity-balance__details">
      <router-link
        v-if="profileDestination"
        :to="profileDestination"
        class="account-identity-balance__name"
      >
        {{ displayName }}
      </router-link>
      <span v-else class="account-identity-balance__name">
        {{ displayName }}
      </span>
      <router-link
        v-if="hasBalance"
        :to="{ name: 'wallet' }"
        class="account-identity-balance__balance"
      >
        {{ formattedBalance }}
      </router-link>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useStore } from "vuex";
import { auth } from "@/firebase";
import Avatar from "@/components/GenericComponents/Avatar.vue";
import {
  formatAvatarDisplayName,
  isAnonymousAvatarUser,
  resolveAvatarName,
  resolveAvatarUid,
} from "@/components/UserComponents/avatarUtils";
import { useAvatarUser } from "@/components/UserComponents/useAvatarUser";
import { walletSummaryService } from "@/services/walletSummaryService";
import { formatAtomicCurrency } from "@/utils/atomicCurrencyFormatting";
import { userProfileRoute } from "@/utils/profilePublicIdentifier";

export default {
  name: "AccountIdentityBalance",
  components: {
    Avatar,
  },
  props: {
    user: {
      type: [Object, Array],
      default: null,
    },
    userUid: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const store = useStore();
    const resolvedUser = useAvatarUser(props);
    const formattedBalance = ref("");
    let requestGeneration = 0;

    const avatarRecords = computed(() => [
      resolvedUser.value?.data,
      resolvedUser.value,
    ]);
    const resolvedUid = computed(
      () => resolveAvatarUid(avatarRecords.value) || props.userUid.trim()
    );
    const isAnonymous = computed(() =>
      isAnonymousAvatarUser(avatarRecords.value)
    );
    const displayName = computed(() =>
      formatAvatarDisplayName(resolveAvatarName(avatarRecords.value))
    );
    // The profile route takes the account's canonical public identifier.
    // Until the store has resolved it for this exact account, the identity is
    // shown without a link rather than falling back to the Firebase UID.
    const profileDestination = computed(() => {
      const uid = resolvedUid.value;
      if (!uid || isAnonymous.value) return null;
      return userProfileRoute(store.getters.ownPublicIdentifierForUid?.(uid));
    });
    const selectedOrgId = computed(
      () => store.getters.selectedOrgUID || null
    );
    const selectedOrgOwnerUid = computed(
      () => store.getters.selectedOrgOwnerUID || null
    );
    const hasBalance = computed(() => formattedBalance.value.length > 0);

    const resetBalance = () => {
      formattedBalance.value = "";
    };

    const requestIsCurrent = ({ generation, uid, orgId }) => {
      return (
        generation === requestGeneration &&
        auth.currentUser?.uid === uid &&
        resolvedUid.value === uid &&
        !isAnonymous.value &&
        selectedOrgId.value === orgId &&
        selectedOrgOwnerUid.value === uid
      );
    };

    const loadActiveWalletSummary = async (
      uid,
      orgId,
      ownerUid,
      { keepShownBalance = false } = {},
    ) => {
      const generation = ++requestGeneration;
      if (!keepShownBalance) resetBalance();
      if (
        !uid ||
        !orgId ||
        isAnonymous.value ||
        ownerUid !== uid ||
        auth.currentUser?.uid !== uid
      ) {
        resetBalance();
        return;
      }

      const request = Object.freeze({ generation, uid, orgId });
      try {
        const summary = await walletSummaryService.getOwnWalletSummary(orgId);
        if (!requestIsCurrent(request)) return;
        if (
          summary?.orgId !== orgId ||
          summary.walletStatus !== "ready" ||
          summary.balances === null ||
          summary.balances.totalAvailableAtomic === "0"
        ) {
          resetBalance();
          return;
        }
        formattedBalance.value = formatAtomicCurrency({
          amountAtomic: summary.balances.totalAvailableAtomic,
          amountScale: summary.balances.amountScale,
          currencySymbol: summary.currency.symbol,
        });
      } catch (_error) {
        if (requestIsCurrent(request)) resetBalance();
      }
    };

    watch(
      [resolvedUid, isAnonymous, selectedOrgId, selectedOrgOwnerUid],
      ([uid, _anonymous, orgId, ownerUid]) =>
        loadActiveWalletSummary(uid, orgId, ownerUid),
      { immediate: true }
    );
    // A payment or Savings move bumps this revision after clearing the
    // summary cache; the shown balance stays until the new one arrives.
    watch(
      () => store.getters.walletBalanceRevision,
      () =>
        loadActiveWalletSummary(
          resolvedUid.value,
          selectedOrgId.value,
          selectedOrgOwnerUid.value,
          { keepShownBalance: true }
        )
    );
    onBeforeUnmount(() => {
      requestGeneration += 1;
      resetBalance();
    });

    return {
      displayName,
      formattedBalance,
      hasBalance,
      profileDestination,
      resolvedUser,
    };
  },
};
</script>

<style scoped>
.account-identity-balance {
  display: flex;
  min-width: 0;
  height: var(--control-height-default);
  align-items: center;
  gap: var(--space-4);
}

.account-identity-balance__avatar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  text-decoration: none;
}

.account-identity-balance__details {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-1);
}

.account-identity-balance__name,
.account-identity-balance__balance {
  color: var(--color-text-primary);
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
}

.account-identity-balance__name {
  max-width: 24ch;
  overflow: hidden;
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
}

.account-identity-balance__balance {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  font-variant-numeric: tabular-nums;
}

</style>
